import { formatDate } from '../../lib/formatters';
import { Form, FormField, FormLabel, FormSelect, Button, Badge } from '../ui';
import LoadingSpinner from '../ui/LoadingSpinner';

interface BookingDetailsProps {
  booking: any;
  isLoading: boolean;
  isEditing: boolean;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onEdit: () => void;
  onClose: () => void;
  isUpdating: boolean;
}

function BookingDetails({
  booking,
  isLoading,
  isEditing,
  onSubmit,
  onEdit,
  onClose,
  isUpdating,
}: BookingDetailsProps) {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge variant="success">Confirmado</Badge>;
      case 'booked':
        return <Badge variant="warning">Reservado</Badge>;
      case 'canceled':
        return <Badge variant="error">Cancelado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-4">
        <p className="text-gray-500">Não foi possível carregar os detalhes do agendamento.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-medium text-gray-500">Cliente</h3>
          <p className="mt-1 text-lg">{booking.client_name}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Status</h3>
          <div className="mt-1">{getStatusBadge(booking.status)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-medium text-gray-500">Serviço</h3>
          <p className="mt-1">{booking.service_name}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Profissional</h3>
          <p className="mt-1">{booking.professional_name}</p>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-500">Contato</h3>
        {booking.contact_name ? (
          <div className="mt-1">
            <p>{booking.contact_name}</p>
            <p className="text-sm text-gray-500">{booking.contact_info}</p>
          </div>
        ) : (
          <p className="mt-1 text-gray-400">Nenhum contato associado</p>
        )}
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-500">Horários</h3>
        <div className="mt-2 space-y-2">
          {booking.slots?.map((slot: any, index: number) => (
            <div
              key={index}
              className="p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
            >
              <div className="flex justify-between">
                <span>{formatDate(slot.date)}</span>
                <span>
                  {slot.start_time} - {slot.end_time}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isEditing && booking.status !== 'canceled' && (
        <Form onSubmit={onSubmit}>
          <FormField>
            <FormLabel htmlFor="status" required>
              Atualizar Status
            </FormLabel>
            <FormSelect id="status" name="status" defaultValue={booking.status} required>
              <option value="booked">Reservado</option>
              <option value="confirmed">Confirmado</option>
              <option value="canceled">Cancelado</option>
            </FormSelect>
          </FormField>

          <div className="mt-4">
            <Button type="submit" isLoading={isUpdating} className="w-full">
              Atualizar Status
            </Button>
          </div>
        </Form>
      )}

      <div className="flex justify-end space-x-3">
        {!isEditing && booking.status !== 'canceled' && (
          <Button onClick={onEdit}>Editar Status</Button>
        )}
        <Button variant="secondary" onClick={onClose}>
          {isEditing ? 'Cancelar' : 'Fechar'}
        </Button>
      </div>
    </div>
  );
}

export default BookingDetails;
